import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	FocusLayer,
	LayerWrapper,
	Layout,
	Richtext,
	RichtextLink,
	ShimLayer,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import * as Icons from '@volkswagen-onehub/icons-core';
import { Close } from '@volkswagen-onehub/icons-core';
import {
	InteractionLayerSizeV2,
	LayerHandleV2,
} from '@volkswagen-onehub/layer-manager';
import { motion, useMotionValueEvent, useScroll } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { getFinanciacionType } from '../helpers/ofertas-helper';
import {
	CarlineWithOfertas,
	FinanceData,
	Oferta,
	Promocion,
} from '../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../tracking/use-tracking-manager';
import { ContentLayer, Gradient } from './layer/content-layer';
import { FinanceLayer } from './layer/finance-layer';
import { OfertaLayer } from './layer/oferta-layer';
import { ModelFrame } from './modelFrame';
import { OfertasCardContainer } from './ofertasCardContainer';
import { ModelSelector } from './selectors/model-selector';
import { MotorizacionSelector } from './selectors/motorizacion-selector';

const OfertasSection = styled.section<{
	numberOfCarlines: number;
	hasOneCarline: boolean;
}>`
	width: 100vw;
	height: ${(props) => Math.floor(props.numberOfCarlines)}00vh;
	position: relative;

	@media screen and (max-width: 1279px) {
		height: ${(props) => props.hasOneCarline && 'fit-content'};
		display: ${(props) => props.hasOneCarline && 'block'};
		position: ${(props) => props.hasOneCarline && 'unset'};
	}

	ul {
		list-style-type: none;
		padding: 0;
	}
`;

const StyledContainer = styled.div`
	overflow: hidden;

	& > div {
		display: flex;
		align-items: center;
	}
`;

const OfertasStickyContainer = styled.div<{ hasOneCarline: boolean }>`
	background: linear-gradient(180deg, #f6f6f6 0%, #dfe4e8 100%);
	position: sticky;
	top: ${(props) => props.theme.size.dynamic0150};
	height: 100vh;

	@media screen and (max-width: 1279px) {
		height: ${(props) => props.hasOneCarline && 'fit-content'};
		display: ${(props) => props.hasOneCarline && 'block'};
		position: ${(props) => props.hasOneCarline && 'unset'};
		align-content: ${(props) => props.hasOneCarline && 'center'};
	}
`;

const RelativeContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;

	position: relative;
`;

const ModelListContainer = styled.div`
	width: 50vw;
	overflow: hidden;
	left: 50px;
	position: relative;
	z-index: 2;

	@media screen and (max-width: 1279px) {
		left: 0;
		width: 100vw;
	}
`;

const ModelFrameContainer = styled.div<{ hasOneCarline: boolean }>`
	position: absolute;
	width: 100vw;
	height: 100vh;
	top: 0;
	left: 0;
	z-index: 1;

	@media screen and (max-width: 1279px) {
		position: ${(props) => props.hasOneCarline && 'unset'};
		height: ${(props) => props.hasOneCarline && 'fit-content'};
	}
`;

const ModelFrameList = styled.div<{ hasOneCarline: boolean }>`
	height: 100vh;
	overflow-x: hidden;
	overflow-y: hidden;

	@media screen and (min-width: 1280px) {
		display: flex;
		align-items: center;
		justify-content: space-evenly;
	}

	@media screen and (max-width: 1279px) {
		height: ${(props) => props.hasOneCarline && 'fit-content'};
	}
`;

const FadeContainer = styled.div<{ numberOfModels: number }>`
	display: flex;
	align-items: center;

	@media screen and (min-width: 960px) {
		height: 30vh;
		margin-bottom: 30px;
		padding-top: ${(props) => props.numberOfModels > 7 && '60px'};

		overflow: hidden;
		-webkit-mask: linear-gradient(
			0deg,
			transparent,
			white 10%,
			white 90%,
			transparent
		);
		mask: linear-gradient(0deg, transparent, white 10%, white 90%, transparent);
	}

	@media screen and (min-width: 1280px) {
		height: 65vh;
		padding-top: ${(props) => props.numberOfModels > 7 && '120px'};
		padding-bottom: 30px;
	}

	@media screen and (min-width: 1600px) {
		height: 80vh;
		padding-top: 0;
	}
`;

const ModelsList = styled.ul`
	transition: margin-top 1s ease; // Define la animación para marginTop

	@media screen and (max-width: 959px) {
		display: none;
	}

	@media screen and (min-width: 960px) and (max-width: 1279px) {
		max-height: 25vh;
	}

	@media screen and (min-width: 1280px) and (max-width: 1599px) {
		max-height: 55vh;
	}

	@media screen and (min-width: 1600px) {
		max-height: 65vh;
	}
`;

const Model = styled.li`
	padding-bottom: ${(props) => props.theme.size.static150};
`;

const WhiteBackgroundContainer = styled.div`
	@media screen and (max-width: 959px) {
		height: 100px;
		background-color: rgb(255, 255, 255);
		display: flex;
		flex-wrap: wrap;
		align-items: flex-end;
		justify-content: center;
	}
`;

const FixedDiv = styled.div`
	position: fixed;
	bottom: 24px;
	left: 50%;
	transform: translateX(-50%);
	background: #ffffff;
	border-radius: 15px;
	z-index: 2;
`;

const slideRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(10px);
  }
`;

const AnimationContainer = styled.div<{ isActive: boolean }>`
	display: inline-block;
	animation: ${({ isActive }) => (isActive ? slideRight : 'none')} 0.5s forwards;
`;

const TrackingContainer = styled.div``;

export const Ofertas: React.FC<{
	carlines: CarlineWithOfertas[];
	hasOneCarline: boolean;
	handleFilteredCarlines: (selectedMotorizacion: string) => void;
	selectedMotorizacion: string;
	setSelectedMotorizacion: React.Dispatch<React.SetStateAction<string>>;
}> = ({
	carlines,
	hasOneCarline,
	handleFilteredCarlines,
	selectedMotorizacion,
	setSelectedMotorizacion,
}) => {
	/* Inicializamos sin carlines a la espera de actualización */
	const [activeCarlineIndex, setActiveCarlineIndex] = useState<number>(-1);
	const [numberOfCarlines, setNumberOfCarlines] = useState<number>(0);

	const [selectedOferta, setSelectedOferta] = useState<Oferta>(null);
	const [selectedPromotion, setSelectedPromotion] = useState<
		Promocion | undefined
	>(undefined);

	/*Variables necesarias para abrir el layer de financiacion*/
	const financeLayerRef = React.useRef<LayerHandleV2<any>>(null);
	const [isFinanceLayerVisible, setIsFinanceLayerVisible] =
		React.useState<boolean>(false);
	const [financeData, setFinanceData] = useState<FinanceData>({
		request: undefined,
		selectedOferta: undefined,
	});

	/*Iniciamos state de control de layer */
	const [isLayerVisible, setIsLayerVisible] = useState(false);
	const [isLayerActive, setIsLayerActive] = useState<boolean>(false);
	const [interactionLayerSize, setInteractionLayerSize] =
		useState<InteractionLayerSizeV2>(InteractionLayerSizeV2.B);

	/* Estado para saber si el componente está en el viewport */
	const [isVisible, setIsVisible] = useState(true);

	/* useRef para controlar sobre que carlines he ejecutado el evento VWBasic_Vehicle_Load */
	const carlineBasicVehicleLoad = useRef(new Set());

	const trackingManager = useTrackingManager();

	/* Variables y funciones auxiliares para scroll controlado */
	let currentScrollDirection = 'down';
	let isAutoScrolling = false;

	function scrollToCarline(scroll: number) {
		isAutoScrolling = true;

		window.scrollTo({
			top: scroll,
		});
		// Utiliza un pequeño retraso para asegurarte de que el scroll se complete
		setTimeout(() => {
			endScrollToCarline();
		}, 0); // El retraso de 0 asegura que el código espere a que el evento de scroll se procese
	}

	function endScrollToCarline() {
		isAutoScrolling = false;
	}
	/* Fin variables y funciones auxiliares para scroll controlado */

	/* Funcion actualizacion carline en Path URL */
	const updateCarlineInPath = () => {
		if (activeCarlineIndex >= 0) {
			if (window.location?.pathname && window.history?.replaceState) {
				const search = window.location.search;
				const splittedPath = window.location.pathname.split('/');
				const carlineInPath = carlines.find((carline) =>
					splittedPath.find((path) => {
						if (path.replace('.app', '') === carline.name) {
							return carline;
						}
					}),
				);

				if (carlineInPath) {
					const index = splittedPath.findIndex((path) =>
						path.includes(carlineInPath.name),
					);
					splittedPath[index] = splittedPath[index].replace(
						carlineInPath.name,
						carlines[activeCarlineIndex].name,
					);
				} else {
					splittedPath.push(`__app/${carlines[activeCarlineIndex].name}.app`);
				}

				const finalPath = splittedPath.join('/');
				window.history.replaceState(null, null, `${finalPath}${search}`);
			}
		}
	};

	/* Funcion para eliminar Carline de URL */
	const cleanCarlineInPath = () => {
		if (window.location?.pathname && window.history?.replaceState) {
			const search = window.location.search;
			const splittedPath = window.location.pathname.split('/');
			const index = splittedPath.findIndex((path) => path.includes('__app'));

			if (index >= 1) {
				splittedPath.splice(index);
				const finalPath = splittedPath.join('/');
				window.history.replaceState(null, null, `${finalPath}${search}`);
			}
		}
	};

	const handleLayerOnClick = () => {
		setIsLayerActive(!isLayerActive);
	};

	/* Inicializamos referencias */
	const sectionRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
	// carlines.forEach((carline) => {
	//  sectionRefs[carline.name] = useRef(null);
	// });

	const ofertasRef = useRef<HTMLDivElement | null>(null);

	/* Variables y funciones para el control del scroll */
	const scrollContainer = document.getElementById('scroll-container');

	const lastModelElement = document.getElementById(
		`model-${numberOfCarlines - 1}`,
	);

	const modelElementHeight = Number(
		lastModelElement &&
			lastModelElement?.getBoundingClientRect().height +
				lastModelElement?.style.marginTop,
	);

	const modelListAutoScroll = () => {
		//Hacemos scroll hasta el carline
		if (activeCarlineIndex >= 0 && scrollContainer) {
			const visibleElements = Math.floor(
				scrollContainer?.getBoundingClientRect().height / modelElementHeight,
			);

			if (visibleElements && scrollContainer) {
				if (visibleElements < numberOfCarlines) {
					if (activeCarlineIndex > visibleElements / 2) {
						const marginTop =
							-1 *
							modelElementHeight *
							(activeCarlineIndex - visibleElements / 2);
						scrollContainer.style.marginTop = `${marginTop}px`;
					} else {
						scrollContainer.style.marginTop = `${modelElementHeight * 2}px`;
					}
				} else {
					scrollContainer.style.marginTop = `${modelElementHeight * 2}px`;
				}
			}
		}
	};
	/* Fin variables y funciones para el control del scroll */

	useEffect(() => {
		if (activeCarlineIndex >= 0) {
			//Actualizamos variable en URL
			updateCarlineInPath();

			//Recalculamos margins de modelList
			modelListAutoScroll();

			//Scorll hasta carline seleccioando
			const viewportHeight = window.innerHeight;
			const rect = ofertasRef.current.getBoundingClientRect();
			const scrollY = window.scrollY; // Obtiene el desplazamiento actual
			const ofertasRefTop = rect.top + scrollY;
			const scrollTo =
				numberOfCarlines > 1
					? ofertasRefTop + viewportHeight * (activeCarlineIndex + 0.5)
					: ofertasRefTop;

			// Validamos si el scrollYProgress concuerda con el activeCarlineIndex, en caso que no lo corregimos
			if (
				(1 / numberOfCarlines) * activeCarlineIndex < scrollYProgress.current &&
				(1 / numberOfCarlines) * (activeCarlineIndex + 1) >
					scrollYProgress.current
			) {
				return;
			} else {
				scrollToCarline(scrollTo);
			}
		}
	}, [activeCarlineIndex]);

	const containerVariants = {
		visible: {
			transition: {
				staggerChildren: 0.2,
			},
		},
	};

	const variants = {
		hidden: { opacity: 0, y: currentScrollDirection === 'up' ? 50 : -50 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.5,
				ease: 'easeInOut',
			},
		},
	};

	/* Control de scroll */
	const { scrollYProgress } = useScroll({
		target: ofertasRef,
	});

	useMotionValueEvent(scrollYProgress, 'change', (value) => {
		if (!isAutoScrolling) {
			const carlineIndex = Math.floor(value * numberOfCarlines);

			if (carlineIndex > numberOfCarlines - 1) {
				return;
			}

			if (carlineIndex !== activeCarlineIndex) {
				const scrollDirection =
					carlineIndex < activeCarlineIndex ? 'up' : 'down';
				currentScrollDirection = scrollDirection;
				setActiveCarlineIndex(carlineIndex);
				setSelectedOferta(carlines[carlineIndex].ofertas[0]);

				if (!carlineBasicVehicleLoad.current.has(carlines[carlineIndex].code)) {
					trackingManager.trackOfertasVehicleLoad({
						CarlineId: carlines[carlineIndex].code.toString(),
						CarlineName: carlines[carlineIndex].title,
						SalesgroupName:
							carlines[carlineIndex].ofertas[0].carline?.salesgroup.title,
						ModelId:
							carlines[carlineIndex].ofertas[0].motorizacionCode?.split('-')[0],
						ModelName:
							carlines[carlineIndex].ofertas[0].carline?.salesgroup.model.name,
						ModelYear:
							carlines[
								carlineIndex
							].ofertas[0].carline?.salesgroup.year.toString(),
						PriceRateType: getFinanciacionType(
							carlines[carlineIndex].ofertas[0],
						),
						PriceRateTotal:
							carlines[carlineIndex].ofertas[0]?.cuota ?? undefined,
						PriceRateCurrency: 'EUR',
						PriceTotalTotal:
							carlines[carlineIndex].ofertas[0]?.precio ?? undefined,
						PriceTotalCurrency: 'EUR',
					});
					carlineBasicVehicleLoad.current.add(carlines[carlineIndex].code);
				}
			}
		}
	});
	/* Fin control de scroll */

	const formatPromotionText = (promotionText: string) => {
		const formatedPromotionText = promotionText.replace(/\n/g, '\n<br>\n');

		return formatedPromotionText;
	};

	const handleTrackingOnClick = (
		carline: CarlineWithOfertas,
		index: number,
	) => {
		setActiveCarlineIndex(index);
		setSelectedOferta(carline.ofertas[0]);

		trackingManager.trackOfertasModelSelectClick({
			CarlineId: carline.code.toString(),
			CarlineName: carline.title,
			SalesgroupName: carline.ofertas[0].carline?.salesgroup.title,
			ModelId: carline.ofertas[0].motorizacionCode?.split('-')[0],
			ModelName: carline.ofertas[0].carline?.salesgroup.model.name,
			ModelYear: carline.ofertas[0].carline?.salesgroup.year.toString(),
			PriceRateType: getFinanciacionType(carline.ofertas[0]),
			PriceRateTotal: carline.ofertas[0]?.cuota ?? undefined,
			PriceRateCurrency: 'EUR',
			PriceTotalTotal: carline.ofertas[0]?.precio ?? undefined,
			PriceTotalCurrency: 'EUR',
		});

		trackingManager.trackOfertasVehiclePageload({
			CarlineId: carline.code.toString(),
			CarlineName: carline.title,
			SalesgroupName: carline.ofertas[0].carline?.salesgroup.title,
			ModelId: carline.ofertas[0].motorizacionCode?.split('-')[0],
			ModelName: carline.ofertas[0].carline?.salesgroup.model.name,
			ModelYear: carline.ofertas[0].carline?.salesgroup.year.toString(),
			PriceRateType: getFinanciacionType(carline.ofertas[0]),
			PriceRateTotal: carline.ofertas[0]?.cuota ?? undefined,
			PriceRateCurrency: 'EUR',
			PriceTotalTotal: carline.ofertas[0]?.precio ?? undefined,
			PriceTotalCurrency: 'EUR',
		});
	};

	/* Reinicialización al cargar los carlines */
	useEffect(() => {
		if (carlines) {
			setNumberOfCarlines(carlines.length);

			const carlineUrlSplitted = window.location.pathname.split('/');
			const carlineUrl = carlineUrlSplitted[
				carlineUrlSplitted.length - 1
			].replace('.app', '');

			const carlineIndex = carlines
				.map(function (carline) {
					return carline.name;
				})
				.indexOf(carlineUrl);

			if (carlineIndex >= 0) {
				setActiveCarlineIndex(carlineIndex);
				setSelectedOferta(carlines[carlineIndex].ofertas[0]);
			} else {
				cleanCarlineInPath();
				setActiveCarlineIndex(0);
				setSelectedOferta(carlines[0].ofertas[0]);
			}
		}
	}, [carlines]);

	/* Observer para controlar si el módulo sale del viewport */
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				// Detecta si el componente está saliendo del viewport
				entries.forEach((entry) => {
					setIsVisible(entry.isIntersecting); // Cambia el estado según la visibilidad del componente
				});
			},
			{
				threshold: 0.1, // Se dispara cuando al menos el 10% del componente está visible
			},
		);

		// Observa el componente
		if (ofertasRef.current) {
			observer.observe(ofertasRef.current);
		}

		// Limpia el observer cuando el componente se desmonte
		return () => {
			if (ofertasRef.current) {
				observer.unobserve(ofertasRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!isVisible) {
			cleanCarlineInPath();
		} else {
			updateCarlineInPath();
		}
	}, [isVisible]);

	useEffect(() => {
		const checkScreenSize = () => {
			if (window.innerWidth < 1280) {
				setInteractionLayerSize(InteractionLayerSizeV2.D);
			}

			if (window.innerWidth > 1279) {
				setInteractionLayerSize(InteractionLayerSizeV2.B);
			}
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => window.removeEventListener('resize', checkScreenSize);
	}, []);

	return (
		<>
			<FinanceLayer
				financeData={financeData}
				setIsFinanceLayerVisible={setIsFinanceLayerVisible}
				isFinanceLayerVisible={isFinanceLayerVisible}
				financeLayerRef={financeLayerRef}
				setIsLayerVisible={setIsLayerVisible}
			/>
			<OfertasSection
				className="ofertas-section"
				numberOfCarlines={numberOfCarlines}
				hasOneCarline={hasOneCarline}
				ref={ofertasRef}
			>
				<ContentLayer
					className="content-layer"
					isLayerVisible={isLayerVisible}
					gradientType={Gradient.LINEAR_GRADIENT_RIGHT}
					layerSize={interactionLayerSize}
				>
					<OfertaLayer
						className="oferta-layer"
						setIsLayerVisible={setIsLayerVisible}
						selectedOferta={selectedOferta}
						activeCarline={carlines[activeCarlineIndex]}
						setIsFinanceLayerVisible={setIsFinanceLayerVisible}
						setFinanceData={setFinanceData}
					/>
				</ContentLayer>
				{activeCarlineIndex >= 0 && (
					<OfertasStickyContainer
						className="ofertas__sticky-container"
						hasOneCarline={hasOneCarline}
					>
						<Layout
							data-testid="layout-ofertas-desktop"
							appearance={{
								[Breakpoints.default]: [{ name: 'a', columns: 24 }],
								[Breakpoints.b960]: hasOneCarline
									? [
											{ name: '.', columns: 0 },
											{ name: 'a', columns: 24 },
											{ name: '.', columns: 0 },
										]
									: [
											{ name: '.', columns: 1 },
											{ name: 'a', columns: 22 },
											{ name: '.', columns: 1 },
										],
								[Breakpoints.b1280]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
							}}
						>
							<ModelListContainer
								className="model-list-container main-container"
								id="main-container"
							>
								<RelativeContainer
									className="relative-container"
									id="relative-container"
								>
									{!hasOneCarline && (
										<WhiteBackgroundContainer className="white-background-container">
											<MotorizacionSelector
												handleFilteredCarlines={handleFilteredCarlines}
												selectedMotorizacion={selectedMotorizacion}
												setSelectedMotorizacion={setSelectedMotorizacion}
											/>
										</WhiteBackgroundContainer>
									)}

									<BreakpointWrapper
										min={Breakpoints.default}
										max={Breakpoints.b960}
									>
										<Container
											data-testid="model-selector-container"
											padding={{
												top: ContainerPadding.dynamic0130,
											}}
											horizontalAlign={'center'}
											shrinkContent
										>
											{!hasOneCarline && (
												<ModelSelector
													carlines={carlines}
													activeCarlineIndex={activeCarlineIndex}
													setActiveCarlineIndex={setActiveCarlineIndex}
													setSelectedOferta={setSelectedOferta}
												/>
											)}
										</Container>
									</BreakpointWrapper>

									{!hasOneCarline && (
										<FadeContainer
											className="fade-container"
											id="fade-container"
											numberOfModels={carlines.length}
										>
											<ModelsList
												className="models-list scroll-container"
												id="scroll-container"
											>
												{carlines.map((carline, index) => {
													const isActive = activeCarlineIndex === index;
													const hashCarline = `#${carline.name}`;
													return (
														<Model
															key={index}
															className={`model-${carline.name}`}
															id={`model-${index}`}
														>
															<TrackingContainer
																className="tracking-div"
																onClick={() =>
																	handleTrackingOnClick(carline, index)
																}
															>
																<RichtextLink href={hashCarline}>
																	<Container
																		wrap={'never'}
																		gutter={ContainerGutter.static100}
																		verticalAlign={'center'}
																	>
																		{isActive && (
																			<Text>
																				<AnimationContainer isActive={isActive}>
																					<Icons.ArrowRight variant="small" />
																				</AnimationContainer>
																			</Text>
																		)}

																		<Text
																			appearance={
																				TokenTextAppearance.headline200
																			}
																			color={
																				isActive
																					? TokenTextColor.primary
																					: TokenTextColor.secondary
																			}
																			bold
																		>
																			<AnimationContainer isActive={isActive}>
																				{carline.nuevo ? 'NUEVO ' : ''}
																				{carline.title.toLocaleUpperCase()}
																			</AnimationContainer>
																		</Text>
																	</Container>
																</RichtextLink>
															</TrackingContainer>
														</Model>
													);
												})}
											</ModelsList>
										</FadeContainer>
									)}
								</RelativeContainer>
							</ModelListContainer>

							<ModelFrameContainer
								className="model-frame-container"
								hasOneCarline={hasOneCarline}
							>
								<ModelFrameList
									className="model-frame-list"
									hasOneCarline={hasOneCarline}
								>
									<motion.div
										initial="hidden"
										animate="visible"
										variants={containerVariants}
									>
										{carlines.map((carline, index) => {
											const isActive = activeCarlineIndex === index;
											return (
												<motion.section
													key={index}
													ref={sectionRefs[carline.name]}
													id={carline.name}
													initial="hidden"
													animate={isActive ? 'visible' : 'hidden'}
													variants={variants}
												>
													<StyledContainer className="prova-styled">
														<ModelFrame
															className={`model-frame-${carline.name}`}
															carline={carlines[activeCarlineIndex]}
															hasOneCarline={hasOneCarline}
															isActive={isActive}
															isLayerVisible={isLayerVisible}
															setIsLayerVisible={setIsLayerVisible}
															selectedOferta={selectedOferta}
															setSelectedOferta={setSelectedOferta}
															handleLayerOnClick={handleLayerOnClick}
															setSelectedPromotion={setSelectedPromotion}
															setIsFinanceLayerVisible={
																setIsFinanceLayerVisible
															}
															setFinanceData={setFinanceData}
														/>
													</StyledContainer>
												</motion.section>
											);
										})}
									</motion.div>
								</ModelFrameList>
							</ModelFrameContainer>
						</Layout>

						<BreakpointWrapper min={Breakpoints.b960} max={Breakpoints.b1280}>
							<Layout
								data-testid="layout-ofertas-tablet"
								appearance={{
									[Breakpoints.default]: [{ name: 'a', columns: 24 }],
									[Breakpoints.b960]: [
										{ name: '.', columns: 1 },
										{ name: 'a', columns: 22 },
										{ name: '.', columns: 1 },
									],
								}}
							>
								<OfertasCardContainer
									className="ofertas-card-container-tablet"
									isMobile={false}
									carline={carlines[activeCarlineIndex]}
									hasOneCarline={hasOneCarline}
									isLayerVisible={isLayerVisible}
									setIsLayerVisible={setIsLayerVisible}
									selectedOferta={selectedOferta}
									setSelectedOferta={setSelectedOferta}
									handleLayerOnClick={handleLayerOnClick}
									setSelectedPromotion={setSelectedPromotion}
									setIsFinanceLayerVisible={setIsFinanceLayerVisible}
									setFinanceData={setFinanceData}
								/>
							</Layout>
						</BreakpointWrapper>
					</OfertasStickyContainer>
				)}
			</OfertasSection>
			{isLayerActive && selectedPromotion?.descripcion && (
				<LayerWrapper contentCentered>
					<>
						<ShimLayer />
						<FocusLayer active size="B" ariaLabel="promotion-layer">
							<Container
								verticalAlign={'flex-end'}
								horizontalAlign={'flex-end'}
								padding={{
									top: {
										[Breakpoints.default]: ContainerPadding.dynamic0100,
										[Breakpoints.b1280]: ContainerPadding.dynamic0250,
										[Breakpoints.b1600]: ContainerPadding.dynamic0100,
									},
									right: ContainerPadding.dynamic0100,
								}}
								stretchContent
							>
								<Text textAlign={TextAlignment.end}>
									<CTA
										tag="button"
										emphasis="secondary"
										size="small"
										onClick={handleLayerOnClick}
										icon={<Close variant="small" />}
									/>
								</Text>
							</Container>
							<Container
								wrap="always"
								padding={{
									top: ContainerPadding.static200,
									bottom: {
										[Breakpoints.default]: ContainerPadding.static500,
										[Breakpoints.b560]: ContainerPadding.static200,
									},
								}}
							>
								<Layout
									data-testid="layout-label"
									appearance={{
										[Breakpoints.default]: [
											{ name: '.', columns: 2 },
											{ name: 'a', columns: 20 },
											{ name: '.', columns: 2 },
										],
										[Breakpoints.b560]: [
											{ name: '.', columns: 1 },
											{ name: 'a', columns: 22 },
											{ name: '.', columns: 1 },
										],
										[Breakpoints.b960]: [
											{ name: '.', columns: 2 },
											{ name: 'a', columns: 20 },
											{ name: '.', columns: 2 },
										],
									}}
								>
									<Richtext
										markdown={formatPromotionText(
											selectedPromotion?.descripcion,
										)}
										dangerouslyParseRawHTML
									/>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.dynamic0100,
										}}
										verticalAlign={'center'}
										stretchContent
									>
										{selectedPromotion.cta.url && (
											<CTA
												tag="a"
												emphasis="secondary"
												href={selectedPromotion.cta.url}
											>
												{selectedPromotion.cta.label}
											</CTA>
										)}
									</Container>
								</Layout>
							</Container>
						</FocusLayer>
						<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b560}>
							<FixedDiv className="fixed-div">
								<CTA
									tag="button"
									emphasis="secondary"
									size="small"
									onClick={handleLayerOnClick}
									icon={<Close variant="small" />}
								>
									Cerrar
								</CTA>
							</FixedDiv>
						</BreakpointWrapper>
					</>
				</LayerWrapper>
			)}
		</>
	);
};
